/**
 * Utility functions for the Callsheet components
 */

import { printCallsheet } from './callsheetPrinter';
import api from '../../../utils/api';
import { API_URL } from '../../../config';

// Format a date to MM/DD/YYYY
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// Format time to HH:MM AM/PM
export const formatTime = (time) => {
  if (!time) return '';
  
  // If time is already in HH:MM AM/PM format, return it
  if (/^\d{1,2}:\d{2}\s?[AP]M$/i.test(time)) {
    return time;
  }
  
  try {
    // Try to parse as Date object if it's a timestamp
    const d = new Date(time);
    if (!isNaN(d.getTime())) {
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return `${hours}:${minutes} ${ampm}`;
    }
  } catch (e) {
    console.error('Error formatting time:', e);
  }
  
  return time;
};

// Format a phone number to (XXX) XXX-XXXX
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  
  return phoneNumber;
};

// Generate ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
export const getOrdinalSuffix = (num) => {
  if (!num) return '';
  
  const j = num % 10;
  const k = num % 100;
  
  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
};

// Sort crew calls by department and call time
export const sortCrewCalls = (crewCalls) => {
  if (!crewCalls || !Array.isArray(crewCalls)) return [];
  
  return [...crewCalls].sort((a, b) => {
    // First sort by department
    if (a.department < b.department) return -1;
    if (a.department > b.department) return 1;
    
    // Then sort by call time if departments are the same
    // Convert call times to comparable values (minutes since midnight)
    const getMinutes = (timeStr) => {
      if (!timeStr) return Number.MAX_SAFE_INTEGER; // Put empty times at the end
      
      const match = timeStr.match(/(\d+):(\d+)\s*([AP]M)/i);
      if (!match) return Number.MAX_SAFE_INTEGER;
      
      let [_, hours, minutes, ampm] = match;
      hours = parseInt(hours);
      minutes = parseInt(minutes);
      
      // Convert to 24-hour format
      if (ampm.toUpperCase() === 'PM' && hours < 12) {
        hours += 12;
      } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
      }
      
      return hours * 60 + minutes;
    };
    
    return getMinutes(a.callTime) - getMinutes(b.callTime);
  });
};

// Save key personnel info to localStorage
export const saveKeyPersonnel = (projectId, { director, producer, dop }) => {
  if (!projectId) return;
  
  try {
    const keyData = { director, producer, dop, lastUpdated: new Date().toISOString() };
    localStorage.setItem(`project_${projectId}_keyPersonnel`, JSON.stringify(keyData));
    console.log('Key personnel saved to localStorage:', keyData);
    return true;
  } catch (err) {
    console.error('Error saving key personnel to localStorage:', err);
    return false;
  }
};

// Get key personnel info from localStorage
export const getKeyPersonnel = (projectId) => {
  if (!projectId) return { director: '', producer: '', dop: '' };
  
  try {
    const storedData = localStorage.getItem(`project_${projectId}_keyPersonnel`);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return {
        director: parsedData.director || '',
        producer: parsedData.producer || '',
        dop: parsedData.dop || ''
      };
    }
  } catch (error) {
    console.error('Error retrieving key personnel from localStorage:', error);
  }
  
  return { director: '', producer: '', dop: '' };
};

// Save location data to localStorage
export const saveLocationData = (projectId, locations) => {
  if (!projectId) return;
  
  try {
    // Only save locations that have both name and address
    const validLocations = locations.filter(loc => loc.name && loc.address);
    
    // Get existing saved locations
    const existingData = getLocationData(projectId);
    
    // Merge with existing data, avoiding duplicates
    const mergedLocations = [...existingData];
    
    validLocations.forEach(newLoc => {
      // Check if this location already exists
      const exists = mergedLocations.some(
        existingLoc => existingLoc.name === newLoc.name && existingLoc.address === newLoc.address
      );
      
      // Add if it doesn't exist
      if (!exists) {
        mergedLocations.push({
          id: newLoc.id || Date.now() + Math.random().toString(36).substr(2, 5),
          name: newLoc.name,
          address: newLoc.address
        });
      }
    });
    
    // Save to localStorage (limit to 20 most recent locations)
    localStorage.setItem(
      `project_${projectId}_locations`, 
      JSON.stringify(mergedLocations.slice(-20))
    );
    
    console.log('Locations saved to localStorage:', mergedLocations);
  } catch (error) {
    console.error('Error saving locations to localStorage:', error);
  }
};

// Get location data from localStorage
export const getLocationData = (projectId) => {
  if (!projectId) return [];
  
  try {
    const storedData = localStorage.getItem(`project_${projectId}_locations`);
    if (storedData) {
      return JSON.parse(storedData) || [];
    }
  } catch (error) {
    console.error('Error retrieving locations from localStorage:', error);
  }
  
  return [];
};

// Default callsheet data structure
export const defaultCallsheet = {
  projectTitle: '',
  company: '',
  productionLogo: '',
  generalCallTime: '',
  date: new Date(),
  shootingDay: 1,
  totalShootingDays: 1,
  director: '',
  producer: '',
  // Multiple producers with types/roles
  producers: [],
  dop: '',
  weatherNotes: '',
  mainLocation: '',
  mainLocationAddress: '',
  mainLocationDirections: '',
  mainLocationNotes: '',
  parkingNotes: '',
  safetyNotes: '',
  generalNotes: '',
  // Per-category notes (shooting schedule departments)
  cameraNotes: '',
  lightingNotes: '',
  propsNotes: '',
  soundNotes: '',
  vfxNotes: '',
  specialFxNotes: '',
  vehiclesNotes: '',
  stuntsNotes: '',
  animalsNotes: '',
  hairMakeupNotes: '',
  miscNotes: '',
  schedule: [],
  crewCalls: [],
  talentCalls: [],
  emergencyContacts: {
    police: '',
    fire: '',
    ambulance: '',
    hospital: '',
    productionOffice: '',
    productionManager: '',
    firstAD: '',
    locationManager: ''
  },
  additionalLocations: [],
  // Basic schedule info block for key day times (server stores as Dates)
  scheduleInfo: {
    crewCall: null,
    talent1stCall: null,
    shootingCall: null,
    estimatedWrap: null,
    lunchTime: null
  }
};

export { printCallsheet };

// Save callsheet to server with smart save logic
export const saveCallsheet = async (callsheet, projectId, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    const { autoSave = false, replaceExisting = true } = options;
    
    // Check if there's an existing callsheet for this shooting day
    let existingCallsheetId = null;
    if (replaceExisting) {
      try {
        const existingResponse = await fetch(`${API_URL}/api/v1/callsheets/project/${projectId}/day/${callsheet.shootingDay}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          }
        });
        
        if (existingResponse.ok) {
          const existingData = await existingResponse.json();
          existingCallsheetId = existingData._id;
          console.log(`📝 Found existing callsheet for Day ${callsheet.shootingDay}, will replace:`, existingCallsheetId);
        } else if (existingResponse.status === 404) {
          // Expected - no callsheet exists for this day yet, will create new one
          console.log(`No existing callsheet for Day ${callsheet.shootingDay}, will create new one`);
        }
      } catch (error) {
        // Network error or other issue
        console.log('Error checking for existing callsheet, will create new one');
      }
    }
    
    // Format and sanitize the data for the API
    const safeDate = callsheet.date instanceof Date ? callsheet.date.toISOString() : new Date(callsheet.date).toISOString();
    const mainLocationEntry = {
      name: callsheet.mainLocation,
      address: callsheet.mainLocationAddress,
      directions: callsheet.mainLocationDirections,
      notes: callsheet.mainLocationNotes
    };
    // Filter out empty additional locations (must at least have a name or address)
    const additionalLocations = Array.isArray(callsheet.additionalLocations)
      ? callsheet.additionalLocations.filter(loc => (loc && (loc.name || loc.address)))
      : [];
    // Only include main location if it has content
    const locations = [];
    if (mainLocationEntry.name || mainLocationEntry.address || mainLocationEntry.directions || mainLocationEntry.notes) {
      locations.push(mainLocationEntry);
    }
    locations.push(...additionalLocations);

    // Helper: convert pages string like "2 4/8" to number (e.g., 2.5)
    const parsePagesToNumber = (val) => {
      if (val === undefined || val === null || val === '') return undefined;
      if (typeof val === 'number') return val;
      if (typeof val !== 'string') return undefined;
      // Trim and split whole and fraction
      const parts = val.trim().split(/\s+/);
      let whole = 0;
      let frac = 0;
      if (parts.length === 1) {
        // Could be "3/8" or "2"
        if (parts[0].includes('/')) {
          const [num, den] = parts[0].split('/').map(n => parseFloat(n));
          if (!isNaN(num) && !isNaN(den) && den !== 0) frac = num / den;
        } else {
          const n = parseFloat(parts[0]);
          if (!isNaN(n)) whole = n;
        }
      } else {
        const w = parseFloat(parts[0]);
        if (!isNaN(w)) whole = w;
        const fracPart = parts[1];
        if (fracPart && fracPart.includes('/')) {
          const [num, den] = fracPart.split('/').map(n => parseFloat(n));
          if (!isNaN(num) && !isNaN(den) && den !== 0) frac = num / den;
        }
      }
      return whole + frac;
    };

    const formattedData = {
      projectId,
      projectTitle: callsheet.projectTitle || '',
      company: callsheet.company || '',
      productionLogo: callsheet.productionLogo || '',
      generalCallTime: callsheet.generalCallTime || '',
      date: safeDate,
      shootingDay: Number(callsheet.shootingDay) || 1,
      totalShootingDays: Number(callsheet.totalShootingDays) || 1,
      director: callsheet.director || '',
      producer: callsheet.producer || '',
      // Persist array of producers if present
      producers: Array.isArray(callsheet.producers)
        ? callsheet.producers
            .filter(p => p && (p.name || p.type))
            .map(p => ({
              type: String(p.type || 'Producer'),
              name: String(p.name || '')
            }))
        : [],
      dop: callsheet.dop || '',
      generalInfo: {
        weatherForecast: callsheet.weatherNotes || '',
        sunriseTime: callsheet?.generalInfo?.sunriseTime || undefined,
        sunsetTime: callsheet?.generalInfo?.sunsetTime || undefined,
        nearestHospital: callsheet?.generalInfo?.nearestHospital || undefined,
        safetyNotes: callsheet?.generalInfo?.safetyNotes || undefined,
      },
      // Keep top-level weatherNotes as well for legacy reads
      weatherNotes: callsheet.weatherNotes || '',
      locations,
      // Persist scheduleInfo (convert Date-like values to ISO if present)
      scheduleInfo: {
        crewCall: callsheet?.scheduleInfo?.crewCall ? new Date(callsheet.scheduleInfo.crewCall).toISOString() : undefined,
        talent1stCall: callsheet?.scheduleInfo?.talent1stCall ? new Date(callsheet.scheduleInfo.talent1stCall).toISOString() : undefined,
        shootingCall: callsheet?.scheduleInfo?.shootingCall ? new Date(callsheet.scheduleInfo.shootingCall).toISOString() : undefined,
        estimatedWrap: callsheet?.scheduleInfo?.estimatedWrap ? new Date(callsheet.scheduleInfo.estimatedWrap).toISOString() : undefined,
        lunchTime: callsheet?.scheduleInfo?.lunchTime ? new Date(callsheet.scheduleInfo.lunchTime).toISOString() : undefined
      },
      // Transform schedule items to match server schema: pages as Number, cast as [String]
      schedule: Array.isArray(callsheet.schedule)
        ? callsheet.schedule.map(item => {
            const pagesNumber = parsePagesToNumber(item.pages);
            // Cast may be a comma-separated string; convert to array of trimmed strings
            let castArray = [];
            if (Array.isArray(item.cast)) {
              castArray = item.cast.filter(Boolean).map(s => String(s));
            } else if (typeof item.cast === 'string') {
              castArray = item.cast
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
            }
            return {
              sceneNumber: item.sceneNumber ?? '',
              setScene: item.setScene ?? item.location ?? '',
              description: item.description ?? '',
              location: item.location ?? item.setScene ?? '',
              locationAddress: item.locationAddress ?? '',
              cast: castArray,
              pages: pagesNumber,
              time: item.time ?? '',
              location_type: item.location_type ?? '',
              oneliner: item.oneliner ?? '',
              estimatedDuration: typeof item.estimatedDuration === 'number' ? item.estimatedDuration : undefined
            };
          })
        : [],
      crewCalls: Array.isArray(callsheet.crewCalls) ? callsheet.crewCalls : [],
      talentCalls: Array.isArray(callsheet.talentCalls) ? callsheet.talentCalls : [],
      // Persist Background / Stand-Ins
      background: Array.isArray(callsheet.background)
        ? callsheet.background.map(bg => ({
            qty: bg.qty ?? '',
            call: bg.call ?? '',
            description: bg.description ?? '',
            notes: bg.notes ?? ''
          }))
        : [],
      notes: {
        general: callsheet.generalNotes || '',
        parking: callsheet.parkingNotes || '',
        safety: callsheet.safetyNotes || '',
        // Map per-category notes to backend keys
        camera: callsheet.cameraNotes || '',
        lighting: callsheet.lightingNotes || '',
        props: callsheet.propsNotes || '',
        sound: callsheet.soundNotes || '',
        vfx: callsheet.vfxNotes || '',
        specialFx: callsheet.specialFxNotes || '',
        vehicles: callsheet.vehiclesNotes || '',
        stunts: callsheet.stuntsNotes || '',
        animals: callsheet.animalsNotes || '',
        hairMakeup: callsheet.hairMakeupNotes || '',
        misc: callsheet.miscNotes || ''
      },
      // Persist structured parking locations (optional)
      parkingLocations: Array.isArray(callsheet.parkingLocations)
        ? callsheet.parkingLocations
            .filter(p => {
              if (!p) return false;
              const hasName = !!p.name;
              const hasAddr = !!p.address;
              const hasPlaceAddr = !!(p.placeData && p.placeData.address);
              const hasCoords = !!(p.placeData && p.placeData.coordinates && typeof p.placeData.coordinates.lat === 'number' && typeof p.placeData.coordinates.lng === 'number');
              return hasName || hasAddr || hasPlaceAddr || hasCoords;
            })
            .map(p => ({
              // Normalize type to server enum: crew | basecamp | productionOffice | other
              type: (() => {
                const raw = String(p.type || '').trim().toLowerCase();
                if (raw === 'crew') return 'crew';
                if (raw === 'basecamp') return 'basecamp';
                if (raw === 'productionoffice' || raw === 'production_office' || raw === 'production office' || raw === 'office') return 'productionOffice';
                return 'other';
              })(),
              name: String(p.name || ''),
              address: String(p.address || ''),
              phone: String(p.phone || ''),
              notes: String(p.notes || ''),
              placeData: p.placeData ? {
                placeId: p.placeData.placeId || undefined,
                address: p.placeData.address || p.address || undefined,
                coordinates: p.placeData.coordinates && typeof p.placeData.coordinates.lat === 'number' && typeof p.placeData.coordinates.lng === 'number'
                  ? { lat: p.placeData.coordinates.lat, lng: p.placeData.coordinates.lng }
                  : undefined
              } : undefined
            }))
        : [],
      emergencyContacts: callsheet.emergencyContacts || {},
      autoSave: !!autoSave,
      savedAt: new Date().toISOString()
    };
    
    // Determine API method and endpoint
    const method = existingCallsheetId ? 'PUT' : 'POST';
    const endpoint = existingCallsheetId ? `${API_URL}/api/v1/callsheets/${existingCallsheetId}` : `${API_URL}/api/v1/callsheets`;
    
    console.log(`💾 ${autoSave ? 'Auto-saving' : 'Saving'} callsheet for Day ${callsheet.shootingDay} (${method})`);
    
    // Make API call to save/update callsheet
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      body: JSON.stringify(formattedData)
    });
    
    if (!response.ok) {
      let errorPayload = {};
      try {
        errorPayload = await response.json();
      } catch (_) {
        try {
          const text = await response.text();
          errorPayload = { raw: text };
        } catch { /* ignore */ }
      }
      console.error('❌ Callsheet save failed', {
        status: response.status,
        statusText: response.statusText,
        error: errorPayload,
        endpoint,
        method,
        formattedData
      });
      throw new Error(
        (errorPayload && (errorPayload.message || errorPayload.error)) ||
        `Failed to ${method === 'PUT' ? 'update' : 'save'} callsheet (${response.status})`
      );
    }
    
    const data = await response.json();
    
    if (autoSave) {
      console.log(`✅ Auto-saved callsheet for Day ${callsheet.shootingDay}`);
    } else {
      console.log(`✅ ${method === 'PUT' ? 'Updated' : 'Saved'} callsheet for Day ${callsheet.shootingDay}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error saving callsheet:', error);
    throw error;
  }
};

// Auto-save callsheet when shooting day advances
export const autoSaveOnDayAdvance = async (callsheet, projectId, previousShootingDay) => {
  try {
    const currentDay = parseInt(callsheet.shootingDay);
    const prevDay = parseInt(previousShootingDay);
    
    // Check if shooting day has advanced
    if (currentDay > prevDay) {
      console.log(`🚀 Shooting day advanced from ${prevDay} to ${currentDay}, auto-saving...`);
      
      // Auto-save the callsheet for the new day
      const result = await saveCallsheet(callsheet, projectId, { 
        autoSave: true, 
        replaceExisting: true 
      });
      
      console.log(`✅ Auto-saved callsheet for advanced shooting day ${currentDay}`);
      return result;
    }
    
    return null;
  } catch (error) {
    console.error('Error in auto-save on day advance:', error);
    throw error;
  }
};

// Get callsheet for specific project and shooting day
export const getCallsheetByDay = async (projectId, shootingDay) => {
  try {
    const response = await api.get(`/callsheets/project/${projectId}/day/${shootingDay}`, {
      validateStatus: (status) => status === 404 || (status >= 200 && status < 300)
    });
    if (response.status === 404) {
      return null;
    }
    return response.data;
  } catch (error) {
    console.error(`Error getting callsheet for day ${shootingDay}:`, error);
    throw error;
  }
};

// Get callsheet from server
export const getCallsheet = async (callsheetId) => {
  try {
    const response = await api.get(`/callsheets/${callsheetId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting callsheet:', error);
    throw error;
  }
};
